import { useRef, createRef } from 'react';
import useCallableRef from './useCallableRef';

let mouseDownCount = 0;
document.body.addEventListener('mousedown', () => mouseDownCount += 1, true);
document.body.addEventListener('mouseup', () => mouseDownCount -= 1, true);

const pressTimeout = 1000;

const createHoverListeners = (onHoverRef, onPressRef, onClickRef, stateRefRef) => {
    let isTapped = false;
    let isHovered = false;
    let isPressed = false;
    let isClicked = false;
    let timerId;

    stateRefRef.current.current = { isHovered, isPressed, isClicked };

    const hover = () => {
        if (!isHovered && !isTapped && mouseDownCount === 0) {
            isHovered = true;
            stateRefRef.current.current = { isHovered, isPressed, isClicked };
            onHoverRef.current(true);
        }
    };

    const leave = () => {
        if (isHovered) {
            isHovered = false;
            stateRefRef.current.current = { isHovered, isPressed, isClicked };
            try {
                onHoverRef.current(false);
            } finally {
                release();
            }
        }
    };

    const release = () => {
        if (isPressed && !isClicked) {
            isPressed = false;
            stateRefRef.current.current = { isHovered, isPressed, isClicked };
            window.clearTimeout(timerId);
            onPressRef.current(false);
        }
    };

    const press = () => {
        if (!isPressed && !isTapped) {
            isPressed = true;
            stateRefRef.current.current = { isHovered, isPressed, isClicked };
            timerId = window.setTimeout(release, pressTimeout);
            onPressRef.current(true);
        }
    };

    const startTap = () => {
        try {
            press();
        } finally {
            isTapped = true;
        }
    };

    const cancelTap = () => {
        isTapped = false;
        release();
    };

    const click = async () => {
        isTapped = false;
        if (isPressed && !isClicked) {
            const finishClickAndRelease = () => { // suppress require-atomic-updates
                isClicked = false;
                stateRefRef.current.current = { isHovered, isPressed, isClicked };
                release();
            };
            try {
                isClicked = true;
                stateRefRef.current.current = { isHovered, isPressed, isClicked };
                const clickResult = onClickRef.current();
                if (clickResult) {
                    const { then } = clickResult;
                    if (typeof then === 'function') {
                        await clickResult;
                    }
                }
            } finally {
                finishClickAndRelease();
            }
        }
    };

    return {
        onMouseEnter: hover,
        onMouseUp: hover,
        onMouseLeave: leave,
        onMouseDown: press,
        onTouchStart: startTap,
        onTouchCancel: cancelTap,
        onTouchMove: cancelTap,
        onClick: click,
        onContextMenu: cancelTap
    };

};

export default (onHover, onPress, onClick, stateRef) => {
    const hoverListenersRef = useRef();
    const onHoverRef = useCallableRef(onHover);
    const onPressRef = useCallableRef(onPress);
    const onClickRef = useCallableRef(onClick);

    const stateRefRef = useRef();
    const currentStateRef = stateRef || createRef();
    const prevStateRef = stateRefRef.current || currentStateRef;
    currentStateRef.current = prevStateRef.current;
    stateRefRef.current = currentStateRef;

    let hoverListeners = hoverListenersRef.current;
    if (hoverListeners === undefined) {
        hoverListeners = createHoverListeners(onHoverRef, onPressRef, onClickRef, stateRefRef);
        hoverListenersRef.current = hoverListeners;
    }
    return hoverListeners;
};
